import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

type Theme = "light" | "dark";

type ThemeContextValue = {
  theme: Theme;
  isDark: boolean;
  toggleTheme: () => void;
};

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

const THEME_STORAGE_KEY = "theme";

function getSystemPrefersDark(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const stored = typeof window !== "undefined" ? (localStorage.getItem(THEME_STORAGE_KEY) as Theme | null) : null;
  const hasStoredPreference = useRef<boolean>(!!stored);

  // Default to light theme when no stored preference is present
  const initialDark = stored ? stored === "dark" : false;
  const [theme, setTheme] = useState<Theme>(initialDark ? "dark" : "light");

  // Apply theme class to <html>
  useEffect(() => {
    const root = document.documentElement;
    if (theme === "dark") root.classList.add("dark");
    else root.classList.remove("dark");
  }, [theme]);

  // Follow system changes unless user explicitly chose a theme
  useEffect(() => {
    const mql = window.matchMedia ? window.matchMedia("(prefers-color-scheme: dark)") : undefined;
    if (!mql) return;
    const onChange = (e: MediaQueryListEvent) => {
      if (!hasStoredPreference.current) {
        setTheme(e.matches ? "dark" : "light");
      }
    };
    try {
      mql.addEventListener("change", onChange);
      return () => mql.removeEventListener("change", onChange);
    } catch {
      // Safari fallback
      mql.addListener(onChange as any);
      return () => mql.removeListener(onChange as any);
    }
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme((prev) => {
      const next = prev === "dark" ? "light" : "dark";
      // Mark explicit preference and persist
      hasStoredPreference.current = true;
      try {
        localStorage.setItem(THEME_STORAGE_KEY, next);
      } catch {}
      return next;
    });
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ theme, isDark: theme === "dark", toggleTheme }),
    [theme, toggleTheme]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
  return ctx;
}

